// src/features/brain/BrainChat.tsx
// Phase 6.6 Bundle 3.2 - Chat orchestrator.
// Bundle 6.7.5 - Stream resilience: incomplete-response handling.
//
// Composes:
//   - useBrainMessages: historical messages for the active conversation
//   - useBrainStream: SSE streaming for new question -> answer
//   - BrainEmptyState: shown when no active conversation
//   - BrainMessage: each rendered bubble (user + assistant)
//
// State machine:
//   1. No active conversation -> show empty state with prompt chips
//   2. User types question -> append optimistic user message
//   3. sendQuestion streams tokens -> live-update streaming assistant message
//   4a. onDone -> conversationId saved, citations attached, invalidate queries
//   4b. onIncomplete (6.7.5) -> dedupe against server, persist the partial
//       if the server has none, keep the text on screen, offer retry
//   5. Loop back to step 2 inside the same conversation
//
// 6.7.5 incomplete-path rules (in order):
//   - Server already has an assistant reply to this question: the done
//     event was lost in transit; the server copy is authoritative. Refetch
//     and discard the client copy.
//   - Server has only our user message: persist the accumulated partial
//     text as the assistant message with metadata {partial: true} via the
//     user-scoped client (RLS verified: org members may INSERT into
//     brain_messages for their own conversations).
//   - No conversation id yet (stream died before the server assigned one):
//     keep the partial on screen only; retry re-sends the question.
//     Closing this gap fully needs an early start event from the ai-router
//     carrying conversation_id; logged as 6.7.5b.
//
// The server-side ai-router handler persists user + assistant messages on
// success, so on success we invalidate ['brain-messages'] to refresh from
// source of truth.

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Send, Square, Loader2, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { useBrainStore } from "@/stores/brainStore";
import {
  useBrainMessages,
  type BrainCitation,
} from "./useBrainQueries";
import { useBrainStream } from "./useBrainStream";
import { BrainEmptyState } from "./BrainEmptyState";
import { BrainMessage } from "./BrainMessage";

interface PendingAssistant {
  content: string;
  citations: BrainCitation[];
}

interface IncompleteState {
  question: string;
  reason: string;
}

// Phase 6.8: [SOURCE:entity_type:entity_id] markers are normally stripped by
// the server before persistence, but client-persisted partials hold raw
// streamed text. Strip complete markers (and a trailing unterminated one)
// before persisting a partial.
function stripSourceMarkers(text: string): string {
  return text
    .replace(/\[SOURCE:[^\]]*\]/g, "")
    .replace(/\[SOURCE:[^\]]*$/, "")
    .trim();
}

export function BrainChat() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const activeConversationId = useBrainStore((s) => s.activeConversationId);
  const setActiveConversationId = useBrainStore(
    (s) => s.setActiveConversationId,
  );

  const [input, setInput] = useState("");
  const [pendingUser, setPendingUser] = useState<string | null>(null);
  const [pendingAssistant, setPendingAssistant] =
    useState<PendingAssistant | null>(null);
  // 6.7.5: set when a stream ended abnormally after content arrived.
  // Drives the incomplete-response banner with the retry action.
  const [incomplete, setIncomplete] = useState<IncompleteState | null>(null);
  // Phase 6.8: when the active stream itself activates a new conversation
  // (done or incomplete on a brand-new conversation), the reset effect must
  // not wipe the pending/incomplete state for that activation.
  const streamOwnedConvRef = useRef<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const messagesQuery = useBrainMessages(activeConversationId);
  const { sendQuestion, isStreaming, cancel } = useBrainStream();

  const messages = messagesQuery.data ?? [];
  const isEmpty =
    !activeConversationId && messages.length === 0 && !pendingUser;

  // Scroll to bottom whenever messages update or streaming progresses.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, pendingAssistant?.content]);

  // Reset pending state when the active conversation changes (e.g. user
  // picked a different conversation from history). Skipped when the change
  // was driven by the active stream itself (Phase 6.8).
  useEffect(() => {
    if (
      activeConversationId &&
      activeConversationId === streamOwnedConvRef.current
    ) {
      streamOwnedConvRef.current = null;
      return;
    }
    setPendingUser(null);
    setPendingAssistant(null);
    setIncomplete(null);
  }, [activeConversationId]);

  async function submit(question: string) {
    const trimmed = question.trim();
    if (!trimmed || isStreaming) return;

    setInput("");
    setIncomplete(null);
    setPendingUser(trimmed);
    setPendingAssistant({ content: "", citations: [] });

    const history = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    await sendQuestion(
      {
        question: trimmed,
        conversationId: activeConversationId,
        locale: i18n.language,
        conversationHistory: history,
      },
      {
        onToken: (text) => {
          setPendingAssistant((prev) =>
            prev
              ? { ...prev, content: prev.content + text }
              : { content: text, citations: [] },
          );
        },
        onDone: ({ conversationId, citations, truncated }) => {
          if (!activeConversationId) {
            streamOwnedConvRef.current = conversationId;
            setActiveConversationId(conversationId);
          }
          // Invalidate so messages refetch from the server. The pending
          // state is cleared by an effect once the real messages arrive.
          queryClient.invalidateQueries({
            queryKey: ["brain-messages", conversationId],
          });
          queryClient.invalidateQueries({ queryKey: ["brain-conversations"] });
          setPendingAssistant((prev) =>
            prev ? { ...prev, citations } : null,
          );
          // Phase 6.8: the server hit max_tokens; the persisted message is
          // complete-as-sent but cut short. Reuse the incomplete banner so
          // the user can retry (e.g. rephrase for a shorter answer).
          if (truncated) {
            setIncomplete({ question: trimmed, reason: "truncated" });
          }
        },
        // 6.7.5: stream ended abnormally after content arrived. Dedupe
        // against the server, persist the partial if needed, keep the
        // text on screen, and show the retry banner.
        onIncomplete: async ({ partialText, conversationId, reason }) => {
          const convId = conversationId ?? activeConversationId;

          if (!convId) {
            // Stream died before even the start event arrived; nothing to
            // persist against. Keep the partial visible client-side only.
            setIncomplete({ question: trimmed, reason });
            return;
          }

          // Phase 6.8: client-persisted partials hold raw streamed text;
          // strip [SOURCE:...] markers the way the server does on success.
          const cleanPartial = stripSourceMarkers(partialText);

          try {
            const { data: recent } = await supabase
              .from("brain_messages")
              .select("id, role, content")
              .eq("conversation_id", convId)
              .order("created_at", { ascending: false })
              .limit(2);

            const last = recent?.[0];
            const prev = recent?.[1];

            const serverHasAssistant =
              last?.role === "assistant" &&
              prev?.role === "user" &&
              prev?.content === trimmed;
            const serverHasUserOnly =
              last?.role === "user" && last?.content === trimmed;

            if (serverHasAssistant) {
              // Only the done event was lost; the server copy is complete
              // and authoritative. Refetch; the catch-up effect clears the
              // pending pair. No banner needed.
            } else if (serverHasUserOnly && cleanPartial.length > 0) {
              // Persist the partial so it survives reloads and appears in
              // history, flagged as partial.
              await supabase.from("brain_messages").insert({
                conversation_id: convId,
                role: "assistant",
                content: cleanPartial,
                metadata: { partial: true, reason },
              });
              setIncomplete({ question: trimmed, reason });
            } else {
              // Server state unexpected; keep the client copy on screen
              // and let the user retry.
              setIncomplete({ question: trimmed, reason });
            }
          } catch {
            setIncomplete({ question: trimmed, reason });
          }

          // Phase 6.8: the start event delivers the id for brand-new
          // conversations, so activate it; the persisted partial then
          // renders from the refetch and history works immediately.
          if (!activeConversationId) {
            streamOwnedConvRef.current = convId;
            setActiveConversationId(convId);
          }

          queryClient.invalidateQueries({
            queryKey: ["brain-messages", convId],
          });
          queryClient.invalidateQueries({ queryKey: ["brain-conversations"] });
        },
        // 6.7.5: onError now fires only when no usable content arrived.
        // Restore the question to the composer so it is not lost.
        onError: (message) => {
          toast.error(`${t("brain.errorTitle")}: ${message}`);
          setPendingUser(null);
          setPendingAssistant(null);
          setInput(trimmed);
        },
      },
    );
  }

  // Once the server-side messages catch up to the pending pair, clear pending.
  useEffect(() => {
    if (!pendingUser || !pendingAssistant) return;
    if (messages.length < 2) return;
    const lastTwo = messages.slice(-2);
    if (
      lastTwo[0]?.role === "user" &&
      lastTwo[0]?.content === pendingUser &&
      lastTwo[1]?.role === "assistant"
    ) {
      setPendingUser(null);
      setPendingAssistant(null);
    }
  }, [messages, pendingUser, pendingAssistant]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submit(input);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends, Shift+Enter inserts newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  }

  function handlePromptPick(prompt: string) {
    submit(prompt);
  }

  function handleRetry() {
    if (!incomplete) return;
    const question = incomplete.question;
    setIncomplete(null);
    submit(question);
  }

  return (
    <div className="flex flex-col h-full">
      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin">
        {isEmpty ? (
          <BrainEmptyState
            onPick={handlePromptPick}
            disabled={isStreaming}
          />
        ) : (
          <>
            {messagesQuery.isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {messages.map((m) => (
              <BrainMessage
                key={m.id}
                role={m.role}
                content={m.content}
                citations={m.citations}
                isPartial={!!(m.metadata?.partial || m.metadata?.truncated)}
              />
            ))}

            {pendingUser && (
              <BrainMessage role="user" content={pendingUser} />
            )}

            {pendingAssistant && (
              <BrainMessage
                role="assistant"
                content={pendingAssistant.content || t("brain.thinking")}
                citations={pendingAssistant.citations}
                isStreaming={isStreaming}
                isPartial={!!incomplete && !isStreaming}
              />
            )}

            {/* 6.7.5: incomplete-response banner with retry */}
            {incomplete && !isStreaming && (
              <div className="flex items-center justify-between gap-2 mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  {t("brain.incompleteNotice")}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className="min-h-[44px] gap-1.5"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {t("brain.retry")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setIncomplete(null)}
                    className="min-h-[44px] min-w-[44px]"
                    aria-label={t("brain.dismiss")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSubmit}
        className="border-t border-border bg-background p-3"
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("brain.askPlaceholder")}
            rows={1}
            disabled={isStreaming}
            className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 min-h-[44px] max-h-32"
          />
          {isStreaming ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={cancel}
              className="shrink-0 min-h-[44px] min-w-[44px]"
              aria-label={t("brain.cancel")}
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon"
              disabled={!input.trim()}
              className="shrink-0 bg-maple-600 hover:bg-maple-700 text-white min-h-[44px] min-w-[44px]"
              aria-label={t("brain.send")}
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
